import { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";
import { FreeMode, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./Accordion.scss";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { AccordionComponent_Plain } from "../../types/components/page/AccordionComponent.ts";
import { useTranslation } from "react-i18next";
import { normalizeLegacyStorageUrl } from "../../utils/utils";

const Accordion = ({
  data,
  title,
}: {
  data: AccordionComponent_Plain[];
  title: string;
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const tabsSwiperRef = useRef<{ swiper: SwiperInstance } | null>(null);

  const [openedVacancies, setOpenedVacancies] = useState<boolean[]>([]);
  const handleTabClick = (index: number) => {
    setActiveTab(index);
    /*setOpenedItems([]);
    if (swiperRef.current) {
      swiperRef.current.swiper.slideTo(0);
    }*/
    if (tabsSwiperRef.current) {
      tabsSwiperRef.current.swiper.slideTo(index);
    }
  };
  useEffect(() => {
    if (data && data.length > 0 && data[activeTab]) {
      setOpenedVacancies(new Array(data[activeTab].items.length).fill(false));
    }
  }, [activeTab, data]);
  const handleDetailsToggle = (index: number) => {
    setOpenedVacancies((prev) => {
      const newOpened = [...prev];
      newOpened[index] = !newOpened[index];
      return newOpened;
    });
  };

  // Effect hook to add target="_blank" to all external links after rendering
  useEffect(() => {
    // Function to check if a URL is external
    const isExternalURL = (url: string): boolean => {
      if (!url) return false;
      // Ignore anchor links and javascript: links
      if (url.startsWith("#") || url.startsWith("javascript:")) return false;
      // Check if the URL is from a different domain
      try {
        const location = window.location;
        const linkHost = new URL(url, location.href).hostname;
        return linkHost !== location.hostname;
      } catch (e) {
        console.log(e);
        // If there's an error parsing the URL, assume it's internal
        return false;
      }
    };

    // Function to set target="_blank" for all external links
    const setExternalLinksTarget = () => {
      // Get the accordion content container
      const accordionContainers = document.querySelectorAll(
        ".accordion-inf-list__cont",
      );

      accordionContainers.forEach((container) => {
        const links = container.querySelectorAll("a");
        links.forEach((link) => {
          const href = link.getAttribute("href");
          if (!href) return;

          const normalizedHref = normalizeLegacyStorageUrl(href);
          if (normalizedHref !== href) {
            link.setAttribute("href", normalizedHref);
          }

          if (isExternalURL(normalizedHref)) {
            link.setAttribute("target", "_blank");
            link.setAttribute("rel", "noopener noreferrer");
          }
        });
      });
    };

    // Set a small timeout to ensure content is rendered
    const timeoutId = setTimeout(() => {
      setExternalLinksTarget();
    }, 100);

    // Clean up the timeout on component unmount
    return () => clearTimeout(timeoutId);
  }, [activeTab, openedVacancies]); // Run this effect when active tab or opened states change

  return (
    <section className="accordion-inf">
      <div className="wrapper">
        <div className="block-title">{title}</div>

        <div className="tabs">
          <Swiper
            ref={tabsSwiperRef}
            modules={[FreeMode, Navigation]}
            freeMode={true}
            slidesPerView="auto"
            spaceBetween={10}
            touchRatio={1}
            navigation
            className="tabs-nav"
          >
            {data.map((country, index) => (
              <SwiperSlide key={country.id} style={{ width: "auto" }}>
                <div
                  className={`tabs-nav__item ${activeTab === index ? "active" : ""}`}
                  onClick={() => handleTabClick(index)}
                  data-index={index}
                >
                  <a href={`#${country.id}`}>
                    <div className="text-desktop">{country.title}</div>
                    <div className="text-mobile">{country.titleShort}</div>
                    <span>{country.items.length}</span>
                  </a>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="tabs-content">
            <div className="accordion-inf-list">
              {data[activeTab]?.items?.map((tabItem, vacancyKey: number) => (
                <div
                  key={vacancyKey}
                  className={`accordion-inf-list__item ${openedVacancies[vacancyKey] ? "opened" : ""}`}
                >
                  <div className="accordion-inf-list__title">
                    {tabItem.title}
                    <div
                      className="show-full"
                      onClick={() => handleDetailsToggle(vacancyKey)}
                    >
                      {openedVacancies[vacancyKey]
                        ? t("Hide details")
                        : t("See details")}
                    </div>
                  </div>
                  <div className="accordion-inf-list__cont">
                    {tabItem.content ? (
                      <BlocksRenderer content={tabItem.content} />
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Accordion;
